import {
  ProgramAttachment,
  ProgramEditFormikModel,
  ProgramSocialMedia,
  SOCIAL_MEDIA_CONTENT_TYPE_CAROUSEL,
  SOCIAL_MEDIA_CONTENT_TYPE_REEL,
  SocialMediaAttachment,
  SocialMediaContentType,
} from '../../types';
import { Form } from 'react-bootstrap';
import { SocialMediaAccountList } from '../social-posting/SocialMediaAccountList';
import { getEmptySocialMedia, getEmptySocialMediaContent } from '../../util';
import { Carousel } from '../social-posting/Carousel';
import {
  GrowegyPlainTextEditor,
  GrowegyPlainTextEditorHandle,
} from '../../../commons/components/GrowegyPlainTextEditor';
import React, { BaseSyntheticEvent, useCallback, useRef, useState } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Overlay, Popover } from 'react-bootstrap';
import { DivButton } from '../../../commons/components/DivButton';
import { SmileyFaceIcon } from '../../../assets/icons';
import { useIsTabletOrLarger } from '../../../responsive/hooks/hooks';
import { ProgramEditSocialMediaContentType } from './ProgramEditSocialMediaContentType';

export const ProgramEditSocialPosting = (props: {
  values: ProgramEditFormikModel;
  canChangeRegularField: boolean;
  disabled: boolean;
  setFieldValue: (
    field: keyof ProgramEditFormikModel,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => void;
  setFieldTouched: (
    field: keyof ProgramEditFormikModel,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void;
}) => {
  const { values, canChangeRegularField, disabled, setFieldValue, setFieldTouched } = props;
  const textEditorRef = useRef<GrowegyPlainTextEditorHandle | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiTargetRef = useRef<HTMLDivElement | null>(null);
  const canEditText = !disabled && canChangeRegularField;
  const terminateEvent = useCallback((e: BaseSyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const isTabletOrLarger = useIsTabletOrLarger();
  const onSocialMediaContentTypeSelected = (type: SocialMediaContentType) => {
    const currentSocialMedia = values.socialMedia ?? getEmptySocialMedia(values.type.role);
    if (currentSocialMedia?.content.type === type) return;
    let newSocialMediaAttachments: SocialMediaAttachment[] | null = null;
    let newProgramAttachments: ProgramAttachment[] | null = null;
    if (type === SOCIAL_MEDIA_CONTENT_TYPE_REEL) {
      let firstVideo: SocialMediaAttachment | null = null;
      newSocialMediaAttachments = [];
      for (const attachment of currentSocialMedia?.content?.attachments ?? []) {
        if (attachment.type === 'VIDEO') {
          firstVideo = attachment;
          newSocialMediaAttachments.push(attachment);
          break;
        }
      }
      newProgramAttachments = values.attachments
        .map((attachment) =>
          attachment.id === firstVideo?.id
            ? { ...attachment }
            : attachment.status === 'NEW'
            ? null
            : { ...attachment, status: 'DELETED' }
        )
        .filter((attachment) => !!attachment) as ProgramAttachment[];
    }
    const newSocialMedia = currentSocialMedia
      ? {
          ...currentSocialMedia,
          content: {
            ...currentSocialMedia.content,
            type,
            attachments: newSocialMediaAttachments ?? currentSocialMedia.content.attachments,
          },
        }
      : null;
    setFieldValue('socialMedia', newSocialMedia, true);
    setFieldTouched('socialMedia', true, true);
    if (newProgramAttachments) {
      setFieldValue('attachments', newProgramAttachments, true);
      setFieldTouched('attachments', true, true);
    }
  };
  return (
    <div>
      <Form.Group controlId="socialMediaAccounts" className="growegy-form__row">
        <label className="growegy-form__label">Publish to</label>
        <SocialMediaAccountList
          posts={values.socialMedia ? values.socialMedia.posts : []}
          content={values.socialMedia?.content ?? getEmptySocialMediaContent()}
          publishOn={values.startDateTime}
          onChanged={(posts) => {
            const currentSocialMedia = values.socialMedia ?? getEmptySocialMedia(values.type.role);
            const newSocialMedia: ProgramSocialMedia | null = currentSocialMedia
              ? {
                  ...currentSocialMedia,
                  posts: posts,
                }
              : null;
            setFieldValue('socialMedia', newSocialMedia, true);
            setFieldTouched('socialMedia', true, true);
          }}
        />
      </Form.Group>
      <Form.Group controlId="socialMediaContentType" className="growegy-form__row">
        <label className="growegy-form__label"></label>
        <ProgramEditSocialMediaContentType
          selectedType={values.socialMedia?.content?.type ?? SOCIAL_MEDIA_CONTENT_TYPE_CAROUSEL}
          onSelected={onSocialMediaContentTypeSelected}
        />
      </Form.Group>
      <Form.Group controlId="socialMediaAttachments" className="growegy-form__row">
        <label className="growegy-form__label">
          {values.socialMedia?.content?.type === SOCIAL_MEDIA_CONTENT_TYPE_REEL
            ? 'Video'
            : 'Images & Video'}
        </label>
        <Carousel
          programId={values.id}
          type={values.socialMedia?.content.type ?? SOCIAL_MEDIA_CONTENT_TYPE_CAROUSEL}
          socialMediaAttachments={values.socialMedia?.content.attachments ?? []}
          attachments={values.attachments}
          onAttachmentIdsChange={(newAttachments) => {
            const currentSocialMedia = values.socialMedia ?? getEmptySocialMedia(values.type.role);
            const newSocialMedia: ProgramSocialMedia | null = currentSocialMedia
              ? {
                  ...currentSocialMedia,
                  content: {
                    ...currentSocialMedia.content,
                    attachments: newAttachments,
                  },
                }
              : null;
            setFieldValue('socialMedia', newSocialMedia, true);
            setFieldTouched('socialMedia', true, true);
          }}
        />
      </Form.Group>
      <Form.Group controlId="socialMediaText" className="growegy-form__row">
        <label className="growegy-form__label">Text</label>
        <div className="flex-grow-1">
          <GrowegyPlainTextEditor
            ref={textEditorRef}
            className="program-edit__social-posting-text"
            initialValue={values.socialMedia?.content.text ? values.socialMedia?.content.text : ''}
            setValue={(text) => {
              const currentSocialMedia =
                values.socialMedia ?? getEmptySocialMedia(values.type.role);
              const newSocialMedia: ProgramSocialMedia | null = currentSocialMedia
                ? {
                    ...currentSocialMedia,
                    content: {
                      ...currentSocialMedia.content,
                      text,
                    },
                  }
                : null;
              setFieldValue('socialMedia', newSocialMedia, true);
              setFieldTouched('socialMedia', true, true);
            }}
            disabled={disabled}
            readOnly={!canChangeRegularField}
            isInvalid={false}
            minRows={5}
          />
          {canEditText && isTabletOrLarger && (
            <div className="d-flex">
              <div
                ref={emojiTargetRef}
                role="button"
                tabIndex={0}
                aria-label="Add emoji"
                onClick={() => setShowEmoji(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setShowEmoji(true);
                }}
              >
                <DivButton isGray={true} icon={<SmileyFaceIcon />} dataTest="program-edit__emoji" />
              </div>
              <Overlay
                show={showEmoji}
                target={emojiTargetRef.current}
                placement="auto"
                rootClose
                onHide={() => setShowEmoji(false)}
              >
                <Popover id="emoji-picker-popover" style={{ zIndex: 1112, maxWidth: 'none' }}>
                  <Popover.Content style={{ padding: 0 }}>
                    <div
                      onClick={terminateEvent}
                      onKeyDown={terminateEvent}
                      onKeyUp={terminateEvent}
                    >
                      <Picker
                        data={data}
                        onEmojiSelect={(emoji: { native: string | undefined | null }) => {
                          const native = emoji?.native;
                          if (!native) return;
                          textEditorRef.current?.insertAtCaret(native);
                          setShowEmoji(false);
                        }}
                        autoFocus={true}
                        theme="light"
                        previewPosition="none"
                      />
                    </div>
                  </Popover.Content>
                </Popover>
              </Overlay>
            </div>
          )}
        </div>
      </Form.Group>
    </div>
  );
};
